import { useRef } from 'react';

export default function ImageHeader({
  image,
  imageHeight,
  onChange,
  onHeightChange,
}: {
  image: string;
  imageHeight: number;
  onChange: (img: string) => void;
  onHeightChange: (height: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        onChange(reader.result);
      }
    };

    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    dragRef.current = {
      startY: event.clientY,
      startHeight: imageHeight,
    };

    window.addEventListener('pointermove', handleResizeMove);
    window.addEventListener('pointerup', handleResizeEnd, { once: true });
  };

  const handleResizeMove = (event: PointerEvent) => {
    const dragState = dragRef.current;

    if (!dragState) {
      return;
    }

    const nextHeight = Math.max(
      20,
      Math.min(400, dragState.startHeight + (event.clientY - dragState.startY)),
    );

    onHeightChange(nextHeight);
  };

  const handleResizeEnd = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', handleResizeMove);
  };

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <div className="relative w-fit">
        <img
          src={image}
          className="w-auto cursor-pointer object-contain"
          style={{ height: `${imageHeight}px` }}
          alt="Image"
          onClick={handleClick}
        />
        <div
          className="absolute -right-2 -bottom-2 h-4 w-4 cursor-ns-resize rounded-full border border-gray-400 bg-white/80 shadow-sm print:hidden"
          title="Drag to resize image"
          onPointerDown={handleResizeStart}
        />
      </div>
    </div>
  );
}
